/**
 * Squelettes de chargement.
 *
 * **Aucun spinner plein écran, jamais.** Un spinner ne dit rien de ce qui
 * arrive et fait clignoter la page à chaque navigation ; un squelette qui
 * reproduit la forme du contenu attendu donne l'impression que la page est
 * déjà là — et elle l'est presque.
 */

interface ProprietesSquelette {
  lignes?: number
  className?: string
}

export function Squelette({ lignes = 3, className = '' }: ProprietesSquelette) {
  return (
    <div className={`space-y-3 ${className}`} aria-busy="true" aria-live="polite">
      <span className="sr-only">Chargement en cours…</span>
      {Array.from({ length: lignes }, (_, i) => (
        <div
          key={i}
          className="h-4 animate-pulse rounded bg-slate-200"
          style={{ width: `${100 - (i % 3) * 12}%` }}
        />
      ))}
    </div>
  )
}

/** Squelette de tableau : mêmes colonnes, mêmes largeurs que le contenu réel. */
export function SqueletteTableau({ colonnes = 4, lignes = 6 }: { colonnes?: number; lignes?: number }) {
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200" aria-busy="true">
      <span className="sr-only">Chargement du tableau…</span>
      <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
        <div className="h-4 w-32 animate-pulse rounded bg-slate-200" />
      </div>
      {Array.from({ length: lignes }, (_, l) => (
        <div key={l} className="grid gap-4 border-b border-slate-100 px-4 py-3 last:border-0"
             style={{ gridTemplateColumns: `repeat(${colonnes}, minmax(0, 1fr))` }}>
          {Array.from({ length: colonnes }, (_, c) => (
            <div key={c} className="h-4 animate-pulse rounded bg-slate-100" />
          ))}
        </div>
      ))}
    </div>
  )
}

/**
 * Squelette de la grille de planning.
 *
 * Il a la forme d'une grille, pas d'une liste : sinon la page saute au moment
 * où les données arrivent, et le saut est perçu comme un défaut.
 */
export function SqueletteGrille() {
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200" aria-busy="true">
      <span className="sr-only">Chargement du planning…</span>
      <div className="grid" style={{ gridTemplateColumns: '4rem repeat(7, minmax(9rem, 1fr))' }}>
        {Array.from({ length: 8 * 9 }, (_, i) => (
          <div key={i} className="h-7 border-b border-r border-slate-100 p-1">
            {i > 7 && i % 3 === 0 ? (
              <div className="h-full animate-pulse rounded bg-slate-100" />
            ) : null}
          </div>
        ))}
      </div>
    </div>
  )
}
